import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, GraduationCap } from 'lucide-react';
import { foundations as f, foundationsFaq } from '../data/programDetails';
import { programs } from '../data/mock';
import StackedHero from '../components/programs/StackedHero';
import FoundationsPillars from '../components/programs/FoundationsPillars';
import FoundationsEnrollBlock from '../components/programs/FoundationsEnrollBlock';
import PhilosophyComparison from '../components/programs/PhilosophyComparison';
import ProgramFaq from '../components/programs/ProgramFaq';

export default function Foundations() {
  return (
    <main data-testid="foundations-page">
      <StackedHero
        image={programs[1].image}
        alt="Coach working one-on-one with a toddler in the pool"
        position={programs[1].imagePosition}
        eyebrow={`Ages ${f.ages} · ${f.subtitle}`}
        title={f.name}
      >
        <p className="mt-4 font-display text-2xl md:text-3xl text-splash-teal leading-snug" data-testid="foundations-tagline">{f.tagline}</p>
        <p className="mt-5 text-splash-green/80 text-lg leading-relaxed" data-testid="foundations-hero-summary">
          20 private 1:1 lessons over 12 weeks, at a pace that respects their feelings.
        </p>
        <a href="#enroll" data-testid="foundations-hero-cta" className="inline-flex items-center gap-2 mt-8 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-7 py-3.5 font-semibold btn-transition">
          See pricing &amp; enrollment <ArrowRight className="w-4 h-4" />
        </a>
        <p className="mt-4 text-xs md:text-sm text-splash-green/60" data-testid="foundations-method-line">{f.method}</p>
      </StackedHero>

      <FoundationsPillars />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Who it&rsquo;s for</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-splash-green">Made for your littlest swimmer</h2>
            <ul className="mt-6 space-y-3">
              {f.whoFor.map((w) => <li key={w} className="flex gap-3 text-splash-green/85"><Check className="w-5 h-5 text-splash-teal shrink-0 mt-0.5" /> {w}</li>)}
            </ul>
            <div className="mt-8 rounded-2xl bg-splash-teal/10 border border-splash-teal/25 p-5 flex gap-3" data-testid="foundations-after">
              <GraduationCap className="w-6 h-6 text-splash-tealDark shrink-0" />
              <p className="text-splash-green/85"><strong className="text-splash-green">After Little Splash Foundations™:</strong> {f.after} Coach Abrie will recommend the best next step for your child.</p>
            </div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">How it works</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-splash-green">Two phases, 20 lessons</h2>
            <div className="mt-6 space-y-4">
              {f.phases.map((p, i) => (
                <div key={p.name} data-testid={`foundations-phase-${i}`} className="rounded-3xl bg-white border border-splash-sand p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-display font-bold text-2xl text-splash-green">{i + 1}. {p.name}</h3>
                    <span className="text-sm font-semibold text-splash-coral">{p.freq} &middot; {p.lessons} lessons</span>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-splash-green/60 mt-1">{p.when}</div>
                  <p className="mt-3 text-splash-green/80">{p.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-splash-green/80" data-testid="foundations-graduation">Graduation is based on our <strong className="text-splash-green">40-objective checklist</strong>, with a <strong className="text-splash-green">16-week flex window</strong> for illness pauses.</p>
          </div>
        </div>
      </section>

      <FoundationsEnrollBlock />
      <PhilosophyComparison />
      <ProgramFaq categories={foundationsFaq} title="Little Splash Foundations™ FAQ" testId="foundations-faq" />

      <section className="pb-16 text-center">
        <Link to="/programs/first-splash" className="inline-flex items-center gap-2 text-splash-teal font-semibold hover:text-splash-tealDark" data-testid="foundations-to-first-splash">
          Under 12 months? See First Splash <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </main>
  );
}
